import {FilterMatchMode, FilterMetadata, LazyLoadEvent, SortMeta} from "primeng/api";

export class Page {
  constructor(init?: Partial<Page>) {
    Object.assign(this, init);
  }

  // The number of elements in the page
  Size: number = 50;
  // The total number of elements
  Total: number = 0;
  // The total number of pages
  Pages: number = 0;
  // The current page number
  Number: number = 0;

  setPage(first: number, rows: number) {
    if (!isNaN(first) && rows > 0) {
      this.Number = first / rows;
      this.Size = rows;
    } else {
      this.Number = 0;
      this.Size = 0;
    }
    return this;
  }
}

export class Sort {
  dir: string
  prop: string
}

export class PagedData<T> {
  Data: T[] = [];
  Page = new Page();
  DistinctSpecs: { Key: string, Value: any[] }[];

  constructor(data: T[], page: Page, distinctSpecs: { Key: string, Value: any[] }[] = null) {
    this.Data = data;
    this.Page = page;
    this.DistinctSpecs = distinctSpecs;
  }
}

export class FilterModel<T> {
  Global?: string;
  Ids?: string[];
  Data?: T;
  Filters?: {
    [s: string]: FilterMetadata | FilterMetadata[];
  } = {}
  AdvancedFilter?: boolean;
  Page? = new Page();
  Sort?: SortMeta[];
  VisibleColumns?: string[] = [];

  constructor(init?: Partial<FilterModel<T>>) {
    Object.assign(this, init);
    this.Data = this.Data || {} as T;
  }

  setSort(event: LazyLoadEvent) {
    if (!event) return;
    if (event.multiSortMeta?.length > 0) {
      this.Sort = event.multiSortMeta;
    } else if (event.sortField) {
      if (event.sortOrder == null) {
        event.sortOrder = 1;
      }

      this.Sort = [{field: event.sortField, order: event.sortOrder} as SortMeta];
    }
  }

  setPage(event: LazyLoadEvent) {
    if (!event) return;
    this.Page.setPage(event.first, event.rows)
  }

  setFilters(filters: { [s: string]: FilterMetadata | FilterMetadata[] }) {
    const specs: { [s: string]: FilterMetadata | FilterMetadata[] } = {};
    for (let key of Object.keys(filters)) {
      let filter = filters[key];

      if (key.startsWith('Specs.')) {
        specs[key.substring(6)] = filter;
      } else {
        let hasFilter = true;
        if (Array.isArray(filter)) {
          hasFilter = filter.some(value => value.value !== null && value.value !== undefined);
        } else {
          hasFilter = filter.value !== null && filter.value !== undefined;
        }

        if (!hasFilter) {
          delete this.Data[key];
          continue;
        }
        this.Data[key] = filter;
      }
    }
    if (Object.keys(filters).length) {
      this.Data['Specs'] = specs;
    }
  }

  static hasAny(filters: { [s: string]: FilterMetadata | FilterMetadata[] }) {
    let hasFilter = false;
    for (let filtersKey in filters) {
      if (filtersKey.startsWith('Specs.')) {
        hasFilter = true;
        break;
      }
      let filter = filters[filtersKey];

      if (Array.isArray(filter)) {
        hasFilter = filter.some(value => value.value !== null && value.value !== undefined);
      } else {
        hasFilter = filter.value !== null && filter.value !== undefined;
      }
      if (hasFilter) {
        break;
      }
    }
    return hasFilter;
  }

  static ensureCustomFilterApplied(customFilter: any, filters: FilterMetadata | FilterMetadata[]) {
    if (customFilter) {
      for (let key of Object.keys(customFilter)) {
        let customFilterElement = customFilter[key];
        if (key === 'Specs') {
          for (let value of customFilterElement) {
            filters[key + '.' + value.PropertyId] = [{
              value: value.Value,
              operator: 'or',
              matchMode: 'equals'
            }];
          }
        } else {
          if (Array.isArray(customFilterElement)) {
            let filterMetadata = [];
            for (let value of customFilterElement) {
              filterMetadata.push({
                value: value,
                operator: 'or',
                matchMode: 'equals'
              });
            }
            filters[key] = filterMetadata;
          } else if (typeof customFilterElement === 'object' && 'matchMode' in customFilterElement) {
            filters[key] = customFilterElement;
          } else {
            filters[key] = [{
              value: customFilterElement,
              operator: 'or',
              matchMode: 'equals'
            }];
          }
        }
      }
    }
  }
}

export class BaseListFilterWithSpecs {
  SelectedSpecs: string[] = [];
  Specs: any;
  ForSelect: boolean = false;
  ShowOnlyRelevantSpecs: FilterMetadata = {value: false};
  IncludeInheritedSpecs: FilterMetadata = {value: false};
  IncludeSpecConstraints: FilterMetadata = {value: false};
  Params: any = {};

  ensureSelectedSpecs() {
    if (!this.SelectedSpecs?.length && this.Specs && (this.Specs?.length || Object.keys(this.Specs)?.length)) {
      for (let key of Object.keys(this.Specs)) {
        this.SelectedSpecs.push(key);
      }
    }
  }
}

export class SpecFilterOptions {
  MatchMode: FilterMatchMode = FilterMatchMode.EQUALS;
  ValueField: boolean = true;
  FormulaField: boolean = true;
  ConstraintsField: boolean = true;
}
